import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  PageHeader,
  Panel,
  Toolbar,
  ToolbarField,
  Modal,
  ConfirmDialog,
  PageSpinner,
  PageError,
} from "../components/admin";
import DataTable from "../components/admin/DataTable";
import useRowActionFeedback from "../components/admin/useRowActionFeedback";

/* ---------------------------------------------------------------------------
   Users — list, filter, edit, reset password, assign subscription, delete.
--------------------------------------------------------------------------- */

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const ROLE_BADGES = {
  admin: "badge-accent",
  manager: "badge-neutral",
  user: "badge-success",
};

/* ── Edit user ─────────────────────────────────────────────────────────── */
function EditUserModal({ user, onClose, onSave }) {
  const [formData, setFormData] = useState(user);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <Modal title={`Edit user · ${user.name || user.username}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Name</label>
            <input name="name" value={formData.name || ""} onChange={handleChange} className="field" />
          </div>
          <div>
            <label className="field-label">Last name</label>
            <input name="lname" value={formData.lname || ""} onChange={handleChange} className="field" />
          </div>
        </div>
        <div>
          <label className="field-label">Email</label>
          <input name="email" type="email" value={formData.email || ""} onChange={handleChange} className="field" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Phone</label>
            <input name="phone" value={formData.phone || ""} onChange={handleChange} className="field" />
          </div>
          <div>
            <label className="field-label">Country</label>
            <input name="country" value={formData.country || ""} onChange={handleChange} className="field" />
          </div>
        </div>
        <div>
          <label className="field-label">Role</label>
          <select name="role" value={formData.role || "user"} onChange={handleChange} className="field">
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary">Save changes</button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Reset password ────────────────────────────────────────────────────── */
function ResetPasswordModal({ user, onClose, onReset }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await onReset(user.userId, password);
      setPassword("");
      setConfirmPassword("");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Reset password · ${user.name || user.username}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="field-label">New password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field"
            placeholder="Enter new password"
            required
            minLength={6}
          />
          <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
        </div>
        <div>
          <label className="field-label">Confirm password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="field"
            placeholder="Confirm new password"
            required
            minLength={6}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Resetting…" : "Reset password"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Assign subscription ───────────────────────────────────────────────── */
function AddSubscriptionModal({ user, onClose, onAssign }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [loading, setLoading] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription-plans`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
        if (data.length > 0) {
          setSelectedPlan(data[0].planId);
          setPlanDetails(data[0]);
        }
      }
    } catch (err) {
      toast.error("Failed to fetch plans");
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handlePlanChange = (planId) => {
    setSelectedPlan(planId);
    setPlanDetails(plans.find((plan) => plan.planId === parseInt(planId)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      toast.error("Please select a plan");
      return;
    }
    setLoading(true);
    try {
      await onAssign(user.userId, selectedPlan);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Assign subscription · ${user.name || user.username}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="field-label">Plan</label>
          <select value={selectedPlan} onChange={(e) => handlePlanChange(e.target.value)} className="field" required>
            {plans.map((plan) => (
              <option key={plan.planId} value={plan.planId}>
                {plan.planName} — {plan.price} · {plan.duration}
              </option>
            ))}
          </select>
        </div>

        {planDetails && (
          <div className="panel p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Duration</span>
              <span className="text-white">{planDetails.duration}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Price</span>
              <span className="text-white">{planDetails.price}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Max votes</span>
              <span className="text-white">{planDetails.maxVotes?.toLocaleString()}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Assigning…" : "Assign subscription"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Main view ─────────────────────────────────────────────────────────── */
function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [resettingUser, setResettingUser] = useState(null);
  const [addingSubscriptionTo, setAddingSubscriptionTo] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);
  const { busyId, flashId, flashKind, run } = useRowActionFeedback();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [countries, setCountries] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleEdit = (user) => setEditingUser(user);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedRole !== "all") params.append("role", selectedRole);
      if (selectedCountry !== "all") params.append("country", selectedCountry);

      const queryString = params.toString();
      const url = `${process.env.REACT_APP_API_URL}/users${queryString ? `?${queryString}` : ""}`;

      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error("Failed to fetch users");
      setUsers(await response.json());
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/countries`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) setCountries(await response.json());
    } catch (err) {
      console.error("Failed to fetch countries:", err);
    }
  };

  const handleSaveEdit = (updatedUser) =>
    run({
      id: updatedUser.userId,
      action: async () => {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${updatedUser.userId}`,
          {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              name: updatedUser.name,
              lname: updatedUser.lname,
              email: updatedUser.email,
              role: updatedUser.role,
              phone: updatedUser.phone,
              country: updatedUser.country,
            }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to update user");
        }
        setEditingUser(null);
      },
      success: "User updated",
      error: "Could not update user",
      refresh: fetchUsers,
    }).catch(() => {});

  const handleResetPassword = (userId, newPassword) =>
    run({
      id: userId,
      action: async () => {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${userId}/reset-password`,
          {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify({ newPassword }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to reset password");
        }
        setResettingUser(null);
      },
      success: "Password reset",
      error: "Could not reset password",
    }).catch(() => {});

  const handleAddSubscription = (userId, planId) =>
    run({
      id: userId,
      action: async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subscriptions/admin/assign`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ userId, planId }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to add subscription");
        }
        setAddingSubscriptionTo(null);
      },
      success: "Subscription assigned",
      error: "Could not assign subscription",
    }).catch(() => {});

  const handleDelete = () =>
    run({
      id: deletingUser.userId,
      action: async () => {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${deletingUser.userId}`,
          { method: "DELETE", headers: getAuthHeaders() }
        );
        if (!response.ok) throw new Error("Failed to delete user");
        setUsers((prev) => prev.filter((u) => u.userId !== deletingUser.userId));
        setDeletingUser(null);
      },
      success: "User deleted",
      error: "Could not delete user",
      flash: "danger",
    }).catch(() => {});

  /* Debounce search input */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* Fetch users when filters change (skip the very first run, mount handles it) */
  const firstRender = React.useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    fetchUsers();
  }, [debouncedSearch, selectedRole, selectedCountry]);

  useEffect(() => {
    fetchUsers();
    fetchCountries();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setMenuUserId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const cols = [
    {
      key: "name",
      label: "User",
      render: (u) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-surface-600 border border-line-strong flex items-center justify-center text-xs font-bold text-accent-soft shrink-0">
            {(u.name || u.username || "U").charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-white font-medium leading-tight">
              {[u.name, u.lname].filter(Boolean).join(" ") || u.username}
            </p>
            <p className="text-xs text-gray-500">@{u.username}</p>
          </div>
        </div>
      ),
    },
    { key: "email", label: "Email", render: (u) => <span className="text-gray-300">{u.email}</span> },
    { key: "phone", label: "Phone", render: (u) => <span className="text-gray-400">{u.phone || "—"}</span> },
    { key: "country", label: "Country", render: (u) => <span className="text-gray-400">{u.country || "—"}</span> },
    {
      key: "role",
      label: "Role",
      render: (u) => <span className={ROLE_BADGES[u.role] || "badge-neutral"}>{u.role || "user"}</span>,
    },
  ];

  if (loading) return <PageSpinner label="Loading users…" />;
  if (error && users.length === 0) return <PageError message={error} onRetry={fetchUsers} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        description={`${users.length} account${users.length === 1 ? "" : "s"} match your filters`}
        actions={
          <button type="button" className="btn-secondary" onClick={fetchUsers}>
            Refresh
          </button>
        }
      />

      <Toolbar>
        <ToolbarField label="Search">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Name, email or username…"
            className="field"
          />
        </ToolbarField>
        <ToolbarField label="Role" className="max-w-[180px]">
          <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="field">
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="user">User</option>
          </select>
        </ToolbarField>
        <ToolbarField label="Country" className="max-w-[200px]">
          <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} className="field">
            <option value="all">All countries</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </ToolbarField>
        <button
          type="button"
          onClick={() => {
            setSearchTerm("");
            setSelectedRole("all");
            setSelectedCountry("all");
          }}
          className="btn-ghost"
        >
          Clear filters
        </button>
      </Toolbar>

      <Panel padded={false}>
        <DataTable
          cols={cols}
          rows={users}
          keyField="userId"
          busyId={busyId}
          flashId={flashId}
          flashKind={flashKind}
          emptyTitle="No users found"
          emptyMessage="Try adjusting your search or filters."
          rowActions={(user) => (
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setMenuUserId(menuUserId === user.userId ? null : user.userId)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-surface-600 transition-colors"
                aria-label="User actions"
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {menuUserId === user.userId && (
                <div className="absolute right-0 top-9 z-30 w-48 bg-surface-800 border border-line-strong rounded-lg shadow-2xl py-1 text-sm">
                  <button
                    className="block w-full text-left px-4 py-2 text-gray-200 hover:bg-surface-700"
                    onClick={() => {
                      setMenuUserId(null);
                      handleEdit(user);
                    }}
                  >
                    Edit user
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-surface-700"
                    onClick={() => {
                      setMenuUserId(null);
                      setResettingUser(user);
                    }}
                  >
                    Reset password
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-surface-700"
                    onClick={() => {
                      setMenuUserId(null);
                      setAddingSubscriptionTo(user);
                    }}
                  >
                    Assign subscription
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 text-accent-soft hover:bg-surface-700"
                    onClick={() => {
                      setMenuUserId(null);
                      setDeletingUser(user);
                    }}
                  >
                    Delete account
                  </button>
                </div>
              )}
            </div>
          )}
        />
      </Panel>

      {/* Modals */}
      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onSave={handleSaveEdit} />
      )}
      {resettingUser && (
        <ResetPasswordModal user={resettingUser} onClose={() => setResettingUser(null)} onReset={handleResetPassword} />
      )}
      {addingSubscriptionTo && (
        <AddSubscriptionModal
          user={addingSubscriptionTo}
          onClose={() => setAddingSubscriptionTo(null)}
          onAssign={handleAddSubscription}
        />
      )}
      {deletingUser && (
        <ConfirmDialog
          title="Delete account"
          message={`Permanently delete ${deletingUser.name || deletingUser.username}? This action cannot be undone.`}
          confirmLabel="Delete account"
          onConfirm={handleDelete}
          onCancel={() => setDeletingUser(null)}
          busy={busyId === deletingUser.userId}
        />
      )}
    </div>
  );
}

export default Users;
